import { motion } from "framer-motion";
import {
  Heartbeat, ShieldChevron, Car, PiggyBank, HouseLine, Buildings, ArrowUpRight,
} from "@phosphor-icons/react";
import { Reveal } from "@/components/Reveal";
import { IMAGES } from "@/constants/site";

const Card = ({ icon: Icon, title, desc, image, span, testid, delay }) => (
  <Reveal delay={delay} className={span}>
    <motion.a
      href="#contact"
      data-testid={testid}
      whileHover={{ y: -6 }}
      transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
      className="group block h-full bg-white border border-black/5 rounded-3xl p-8 md:p-10 shadow-sm hover:shadow-xl hover:shadow-forest/10 transition-shadow duration-500"
    >
      <div className="flex items-start justify-between">
        <span className="w-12 h-12 rounded-2xl bg-mint-pale text-forest flex items-center justify-center">
          <Icon size={24} weight="duotone" />
        </span>
        <ArrowUpRight size={22} weight="bold"
          className="text-mint opacity-0 -translate-x-2 translate-y-2 group-hover:opacity-100 group-hover:translate-x-0 group-hover:translate-y-0 transition-all duration-300" />
      </div>
      {image && (
        <div className="mt-6 rounded-xl overflow-hidden">
          <img src={image} alt={title} loading="lazy"
            className="w-full h-44 object-cover group-hover:scale-105 transition-transform duration-700" />
        </div>
      )}
      <h3 className="mt-6 font-serif text-2xl md:text-3xl text-ink">{title}</h3>
      <p className="mt-3 text-sm md:text-base text-moss leading-relaxed">{desc}</p>
    </motion.a>
  </Reveal>
);

export const Services = () => (
  <section id="services" className="py-24 md:py-32 bg-linen" data-testid="services-section">
    <div className="max-w-7xl mx-auto px-6 md:px-12">
      <Reveal>
        <p className="text-xs font-bold tracking-[0.25em] uppercase text-mint">What we do</p>
        <h2 className="mt-4 font-serif text-4xl sm:text-5xl tracking-tight text-ink max-w-2xl text-balance">
          Protection & finance for every chapter of life
        </h2>
      </Reveal>

      <div className="mt-16 grid grid-cols-1 md:grid-cols-12 gap-6">
        <Card span="md:col-span-7" icon={Heartbeat} title="Medical Insurance" testid="service-card-medical" image={IMAGES.medical} delay={0}
          desc="Cashless hospitalisation, family floaters and senior-citizen plans compared across insurers — so a medical emergency never becomes a financial one." />
        <Card span="md:col-span-5" icon={ShieldChevron} title="Life & Term Insurance" testid="service-card-life" delay={0.1}
          desc="Pure term plans and life cover sized to your family's real needs, with honest guidance on riders and claim ratios." />
        <Card span="md:col-span-4" icon={Car} title="Motor Insurance" testid="service-card-motor" delay={0}
          desc="Comprehensive car & bike cover with fast renewals, add-ons and claim assistance when you need it most." />
        <Card span="md:col-span-4" icon={PiggyBank} title="Pension Plans" testid="service-card-pension" delay={0.1}
          desc="Retirement and annuity plans that turn today's savings into tomorrow's steady income." />
        <Card span="md:col-span-4" icon={HouseLine} title="Home Loans" testid="service-card-home-loan" delay={0.2}
          desc="Home loans matched across major banks & NBFCs — better rates, faster approvals, zero running around." />
        <Card span="md:col-span-7" icon={Buildings} title="Commercial Property Loans" testid="service-card-commercial" image={IMAGES.commercial} delay={0}
          desc="Funding for shops, offices and commercial spaces, structured with lending partners who understand business realities." />
        <Reveal delay={0.1} className="md:col-span-5">
          <motion.a href="#contact" data-testid="service-card-cta" whileHover={{ y: -6 }}
            transition={{ duration: 0.35, ease: [0.22, 1, 0.36, 1] }}
            className="group flex flex-col justify-between h-full bg-forest text-white rounded-3xl p-8 md:p-10">
            <h3 className="font-serif text-2xl md:text-3xl leading-snug">Not sure where to start?</h3>
            <div className="mt-8">
              <p className="text-sm text-white/70 leading-relaxed">Tell us your goal — we'll recommend the right cover or loan, free of charge.</p>
              <span className="mt-6 inline-flex items-center gap-2 text-mint-soft font-semibold text-sm">
                Talk to an advisor
                <ArrowUpRight size={18} weight="bold" className="group-hover:translate-x-1 group-hover:-translate-y-1 transition-transform duration-300" />
              </span>
            </div>
          </motion.a>
        </Reveal>
      </div>
    </div>
  </section>
);
